export type Row = readonly [number, number, number, string];
export type BsYmd = { y: number; m: number; d: number };
export type Cal = { rows: Map<string, { days: number; adStart: string }> };

const key = (y: number, m: number) => `${y}-${m}`;

export function fromRows(rows: readonly Row[]): Cal {
  return { rows: new Map(rows.map(([y, m, days, adStart]) => [key(y, m), { days, adStart }])) };
}

let cached: Promise<Cal> | undefined;
let syncCal: Cal | undefined;

try {
  const stored = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("dhuwani-cal") : null;
  if (stored) syncCal = fromRows(JSON.parse(stored));
} catch {}

export function getSyncCalendar(): Cal | undefined {
  return syncCal;
}

/** Same table as the server (GET /api/v1/calendar/table.json, seeded from CargoBooks Date_Table). */
export function loadCalendar(): Promise<Cal> {
  if (syncCal) return Promise.resolve(syncCal);
  cached ??= fetch("/api/v1/calendar/table.json")
    .then((r) => r.json() as Promise<Row[]>)
    .then((rows) => {
      try { sessionStorage.setItem("dhuwani-cal", JSON.stringify(rows)); } catch {}
      syncCal = fromRows(rows);
      return syncCal;
    });
  return cached;
}

export function daysIn(cal: Cal, y: number, m: number): number {
  return cal.rows.get(key(y, m))?.days ?? 0;
}

export function toAd(cal: Cal, d: BsYmd): string | null {
  const row = cal.rows.get(key(d.y, d.m));
  if (!row || d.d < 1 || d.d > row.days) return null;
  const t = new Date(`${row.adStart}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + d.d - 1);
  return t.toISOString().slice(0, 10);
}

export function addDays(cal: Cal, d: BsYmd, n: number): BsYmd | null {
  let { y, m, d: day } = d;
  day += n;
  while (day < 1) {
    m -= 1; if (m < 1) { m = 12; y -= 1; }
    const len = daysIn(cal, y, m); if (!len) return null;
    day += len;
  }
  for (let len = daysIn(cal, y, m); len && day > len; len = daysIn(cal, y, m)) {
    day -= len; m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return daysIn(cal, y, m) ? { y, m, d: day } : null;
}

export function fmt(d: BsYmd): string {
  return `${String(d.y).padStart(4, "0")}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
}

/** Server-rendered NPT today (body[data-kit-today]); the browser clock is never used (CAL-04). */
export function todayBs(): BsYmd {
  const [y, m, d] = (document.body.dataset.kitToday ?? "").split("-").map(Number);
  return { y: y ?? 0, m: m ?? 0, d: d ?? 0 };
}
